/**
 * Onboarding Context
 * 
 * Manages user onboarding progress through a 3-step flow:
 * - Step 1: Connect Google Calendar
 * - Step 2: Create Automation Rules
 * - Step 3: Setup Analytics
 * 
 * Each step can be "done", "skipped", or null (not started)
 */

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './auth-context'

export type OnboardingStepStatus = 'done' | 'skipped' | null

export interface OnboardingState {
  step1: OnboardingStepStatus
  step2: OnboardingStepStatus
  step3: OnboardingStepStatus
}

interface OnboardingContextType {
  /** Current onboarding state, null if not loaded */
  onboarding: OnboardingState | null
  /** Loading state for initial fetch */
  loading: boolean
  /** Whether all 3 steps are complete (done or skipped) */
  isComplete: boolean
  /** The current step (first step that is null), or 3 if step 1 & 2 are done */
  currentStep: 1 | 2 | 3
  /** Mark a step as "done" */
  completeStep: (step: 1 | 2 | 3) => Promise<void>
  /** Mark a step as "skipped" */
  skipStep: (step: 1 | 2 | 3) => Promise<void>
  /** Re-fetch onboarding state from database */
  refetch: () => Promise<void>
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

// Default state for new users
const getDefaultOnboarding = (): OnboardingState => ({
  step1: null,
  step2: null,
  step3: null,
})

// Load onboarding state from database
const loadOnboardingFromDB = async (userId: string): Promise<OnboardingState | null> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('onboarding')
      .eq('id', userId)
      .single()

    if (error) {
      console.warn('Failed to load onboarding from database:', error)
      return null
    }

    // If onboarding is null in DB, user hasn't started onboarding
    if (!data.onboarding) {
      return getDefaultOnboarding()
    }

    // Parse JSONB data
    return {
      step1: data.onboarding.step1 ?? null,
      step2: data.onboarding.step2 ?? null,
      step3: data.onboarding.step3 ?? null,
    }
  } catch (error) {
    console.warn('Failed to load onboarding from database:', error)
    return null
  }
}

// Save onboarding state to database
const saveOnboardingToDB = async (userId: string, onboarding: OnboardingState): Promise<void> => {
  try {
    const { error } = await supabase
      .from('users')
      .update({
        onboarding: onboarding,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)

    if (error) {
      console.warn('Failed to save onboarding to database:', error)
      throw error
    }
  } catch (error) {
    console.warn('Failed to save onboarding to database:', error)
    throw error
  }
}

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const [onboarding, setOnboarding] = useState<OnboardingState | null>(null)
  const [loading, setLoading] = useState(true)
  
  // Track if we've loaded for the current user to prevent race conditions
  const loadedForUserRef = useRef<string | null>(null)

  // Load onboarding state from database when user changes
  const fetchOnboarding = useCallback(async () => {
    // Wait for auth to complete before making decisions
    if (authLoading) {
      return
    }

    // No user - clear state and mark as loaded
    if (!user?.id) {
      setOnboarding(null)
      setLoading(false)
      loadedForUserRef.current = null
      return
    }

    // Already loaded for this user - skip
    if (loadedForUserRef.current === user.id) {
      return
    }

    setLoading(true)
    try {
      const dbOnboarding = await loadOnboardingFromDB(user.id)
      setOnboarding(dbOnboarding || getDefaultOnboarding())
      loadedForUserRef.current = user.id
    } catch (error) {
      console.warn('Failed to load onboarding:', error)
      setOnboarding(getDefaultOnboarding())
      loadedForUserRef.current = user.id
    } finally {
      setLoading(false)
    }
  }, [user?.id, authLoading])

  // Fetch onboarding on mount and when user/auth changes
  useEffect(() => {
    fetchOnboarding()
  }, [fetchOnboarding])

  // Check if all steps are complete (done or skipped)
  const isComplete = useMemo(() => {
    if (!onboarding) return false
    return (
      onboarding.step1 !== null &&
      onboarding.step2 !== null &&
      onboarding.step3 !== null
    )
  }, [onboarding])

  // Determine the current step (first step that is null)
  const currentStep = useMemo((): 1 | 2 | 3 => {
    if (!onboarding || onboarding.step1 === null) return 1
    if (onboarding.step2 === null) return 2
    return 3
  }, [onboarding])

  // Mark a step as "done"
  const completeStep = useCallback(async (step: 1 | 2 | 3) => {
    if (!user?.id || !onboarding) return

    const stepKey = `step${step}` as keyof OnboardingState
    const newOnboarding = { ...onboarding, [stepKey]: 'done' as OnboardingStepStatus }
    
    // Update state optimistically
    setOnboarding(newOnboarding)

    try {
      await saveOnboardingToDB(user.id, newOnboarding)
    } catch (error) {
      // Revert on error
      setOnboarding(onboarding)
      throw error
    }
  }, [user?.id, onboarding])

  // Mark a step as "skipped"
  const skipStep = useCallback(async (step: 1 | 2 | 3) => {
    if (!user?.id || !onboarding) return

    const stepKey = `step${step}` as keyof OnboardingState
    const newOnboarding = { ...onboarding, [stepKey]: 'skipped' as OnboardingStepStatus }
    
    // Update state optimistically
    setOnboarding(newOnboarding)

    try {
      await saveOnboardingToDB(user.id, newOnboarding)
    } catch (error) {
      // Revert on error
      setOnboarding(onboarding)
      throw error
    }
  }, [user?.id, onboarding])

  // Force refetch function that clears the cache
  const refetch = useCallback(async () => {
    loadedForUserRef.current = null
    await fetchOnboarding()
  }, [fetchOnboarding])

  const value = useMemo(() => ({
    onboarding,
    loading,
    isComplete,
    currentStep,
    completeStep,
    skipStep,
    refetch,
  }), [onboarding, loading, isComplete, currentStep, completeStep, skipStep, refetch])

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}

